import { createAction } from '@reduxjs/toolkit';

import type {
    DashboardVideo,
    EstimatedRenderDurationResponse,
    ProjectColorCode,
    ProjectIcon,
    VideoSortBy,
} from 'js/src/client';
import type { View } from 'js/src/data/dashboard/dashboard-types';
import { DASHBOARD_ACTIONS } from 'js/src/data/dashboard/dashboard-types';
import type { DeepReadonly } from 'js/src/libs/consts';

export type StoreVideosPayload = {
    view: number;
    videos: readonly DeepReadonly<DashboardVideo>[];
    addToEnd: boolean;
};
export const storeVideos = createAction<StoreVideosPayload>(DASHBOARD_ACTIONS.STORE_VIDEOS);

export type StoreLoadedUpToPagePayload = {
    view: number;
    loadedUpToPage: number;
};
export const storeLoadedUpToPage = createAction<StoreLoadedUpToPagePayload>(
    DASHBOARD_ACTIONS.STORE_LOADED_PAGE_NUMBER,
);

export type StoreVideosSortByPayload = {
    view: number;
    videosSortBy: VideoSortBy;
};
export const storeVideosSortBy = createAction<StoreVideosSortByPayload>(
    DASHBOARD_ACTIONS.STORE_VIDEOS_SORT_BY,
);

export type storeVideoFilterByUserIdPayload = {
    view: number;
    userId: number | undefined;
};
export const storeVideosFilterByUserId = createAction<storeVideoFilterByUserIdPayload>(
    DASHBOARD_ACTIONS.STORE_VIDEOS_FILTER_BY_USER_ID,
);

export type StoreMoreVideosToLoadPayload = {
    view: number;
    hasMoreVideos: boolean;
};
export const storeMoreVideosToLoad = createAction<StoreMoreVideosToLoadPayload>(
    DASHBOARD_ACTIONS.STORE_MORE_VIDEOS_TO_LOAD,
);

// updateVideoProperty is a generic action that can update *any* primitive property of the videos in the views
// this method iterates over all the views and updates the video anywhere where it is currently stored.
export type UpdateVideoPropertyPayload = {
    videoId: number;
    property: keyof DashboardVideo;
    newValue: number | string | null | boolean | readonly string[];
};
export const updateVideoProperty = createAction<UpdateVideoPropertyPayload>(
    DASHBOARD_ACTIONS.UPDATE_VIDEO_PROPERTY,
);

export type DeleteVideoFromViewPayload = {
    view: number;
    videoId: number;
};
export const deleteVideoFromView = createAction<DeleteVideoFromViewPayload>(
    DASHBOARD_ACTIONS.DELETE_VIDEO_FROM_VIEW,
);

export type DeleteVideoFromStorePayload = {
    videoId: number;
};
export const deleteVideoFromStore = createAction<DeleteVideoFromStorePayload>(
    DASHBOARD_ACTIONS.DELETE_VIDEO,
);

export type ClearVideosPayload = {
    view: number;
};
export const clearVideos = createAction<ClearVideosPayload>(DASHBOARD_ACTIONS.CLEAR_VIDEOS);

export type AddViewsPayload = {
    views: readonly View[];
};
export const addViews = createAction<AddViewsPayload>(DASHBOARD_ACTIONS.ADD_VIEWS);

export type SetActiveViewPayload = {
    view: number;
};
export const setActiveView = createAction<SetActiveViewPayload>(DASHBOARD_ACTIONS.SET_ACTIVE_VIEW);

export const updateViewMetadata = createAction<{
    view: number;
    name?: string;
    color?: ProjectColorCode;
    icon?: ProjectIcon;
}>(DASHBOARD_ACTIONS.UPDATE_VIEW_METADATA);

export type RemoveViewPayload = {
    view: number;
};
export const removeView = createAction<RemoveViewPayload>(DASHBOARD_ACTIONS.REMOVE_VIEW);

/**
 **** < Related to Video Search > ****
 */
export const setVideoSearchTerm = createAction<string>(DASHBOARD_ACTIONS.SET_VIDEO_SEARCH_TERM);

/**
 **** < Related to Video Search /> ****
 */
export const setBulkActionSpinner = createAction<boolean>(
    DASHBOARD_ACTIONS.SET_BULK_ACTION_SPINNER,
);

export const storeRenderEstimates = createAction<
    EstimatedRenderDurationResponse['video_estimates']
>(DASHBOARD_ACTIONS.STORE_RENDER_ESTIMATES);

export const setLoadingDashboard = createAction<boolean>(DASHBOARD_ACTIONS.SET_LOADING_DASHBOARD);
